import { useState, useEffect, useRef, useCallback } from 'react';
import { findArtistTrack } from '../api/musicClient';
import { flushQueue as flushDeezerQueue } from '../api/deezerClient';

const CONCURRENCY = 5;

/**
 * Hook to batch-fetch Deezer tracks for an array of galaxy nodes.
 * Returns tracks incrementally as they are fetched.
 *
 * @param {Array} nodes - Galaxy nodes to fetch tracks for
 * @returns {{ tracks: Map, progress: { fetched: number, total: number }, isLoading: boolean }}
 */
export function useExportTracks(nodes) {
  const [tracks, setTracks] = useState(new Map());
  const [progress, setProgress] = useState({ fetched: 0, total: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const fetchedRef = useRef(new Map()); // persistent across re-renders

  useEffect(() => {
    // Each run owns its own cancellation flag so a filter change mid-load
    // can't have its state writes clobbered by the previous run finishing.
    let cancelled = false;

    if (!nodes || nodes.length === 0) {
      setTracks(new Map());
      setProgress({ fetched: 0, total: 0 });
      setIsLoading(false);
      return () => { cancelled = true; };
    }

    setIsLoading(true);

    // Reuse already-fetched tracks
    const result = new Map();
    const toFetch = [];
    for (const node of nodes) {
      if (fetchedRef.current.has(node.id)) {
        result.set(node.id, fetchedRef.current.get(node.id));
      } else {
        toFetch.push(node);
      }
    }

    setTracks(new Map(result));
    setProgress({ fetched: result.size, total: nodes.length });

    // Flush any queued background requests so the playlist's fetches
    // aren't stuck behind explore prefetches or detail-panel enrichment.
    flushDeezerQueue();

    (async () => {
      let fetched = result.size;

      for (let i = 0; i < toFetch.length; i += CONCURRENCY) {
        if (cancelled) return;

        const batch = toFetch.slice(i, i + CONCURRENCY);
        const batchResults = await Promise.allSettled(
          batch.map((node) => findArtistTrack(node.name, node.id, { priority: true }))
        );

        // Always cache the fetched data — even if this run was cancelled,
        // the tracks are still valid for future filter selections.
        batch.forEach((node, idx) => {
          const res = batchResults[idx];
          if (res.status === 'fulfilled' && res.value) {
            fetchedRef.current.set(node.id, res.value);
            result.set(node.id, res.value);
          }
        });

        if (cancelled) return;

        fetched += batch.length;
        setTracks(new Map(result));
        setProgress({ fetched: Math.min(fetched, nodes.length), total: nodes.length });
      }

      if (!cancelled) setIsLoading(false);
    })();

    return () => { cancelled = true; };
  }, [nodes]);

  // Re-fetch a single track to get a fresh preview URL (e.g. when a CDN
  // URL has expired).  Returns the fresh track or null.
  const refetchTrack = useCallback(async (nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return null;

    // Clear stale cache entry so we hit the API again
    fetchedRef.current.delete(nodeId);

    try {
      const fresh = await findArtistTrack(node.name, node.id, { priority: true });
      if (fresh) {
        fetchedRef.current.set(nodeId, fresh);
        setTracks((prev) => {
          const next = new Map(prev);
          next.set(nodeId, fresh);
          return next;
        });
      }
      return fresh;
    } catch {
      return null;
    }
  }, [nodes]);

  return { tracks, progress, isLoading, refetchTrack };
}
